import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { bewaarTaal, bewaardeTaal, geldigeTaal, vertaal } from "@/lib/taal";

/**
 * De taal van de app (NL/EN-batch A1). Zonder provider de bewaarde taal, standaard NL, zodat
 * een component in een toets zonder omhulling gewoon Nederlands tekent.
 */
/**
 * Zonder provider geldt de taal die deze browser onthield, standaard Nederlands.
 * In de app staat er altijd een provider; dit is voor toetsen die een losse
 * component tekenen. Een toets die het Engelse scherm beschrijft, zet
 * `novamund_taal` op "en", onderaan het bestand (na een eventuele clear).
 */
const TaalContext = createContext({
  get taal() {
    return bewaardeTaal();
  },
  zetTaal: () => {},
  t: (sleutel, waarden) => vertaal(bewaardeTaal(), sleutel, waarden),
});

/**
 * DE SERVER IS DE GEDEELDE WAARHEID, DE BROWSER HET EERSTE BEELD. Bij het laden
 * geldt eerst wat deze browser onthield; zodra er een gebruiker is wint wat de
 * server zegt, want de keuze op de Mac hoort ook op de telefoon te gelden.
 * Schakelen schrijft beide meteen, zonder opslaan-knop.
 */
export function TaalProvider({ children, gebruiker = null, lees = axios.get, schrijf = axios.put }) {
  const [taal, setTaal] = useState(bewaardeTaal);

  useEffect(() => {
    if (!gebruiker) return undefined;
    let weg = false;
    lees(`${API}/settings`)
      .then((r) => {
        const opServer = r?.data?.taal;
        if (!weg && opServer) {
          const t = geldigeTaal(opServer);
          setTaal(t);
          bewaarTaal(t);
        }
      })
      .catch(() => {});
    return () => {
      weg = true;
    };
  }, [gebruiker, lees]);

  useEffect(() => {
    try {
      document.documentElement.lang = taal;
    } catch {
      // geen document in een kale omgeving
    }
  }, [taal]);

  const zetTaal = useCallback(
    (nieuw) => {
      const t = geldigeTaal(nieuw);
      setTaal(t);
      bewaarTaal(t);
      return Promise.resolve(schrijf(`${API}/settings`, { taal: t })).catch(() => {});
    },
    [schrijf]
  );

  const waarde = useMemo(
    () => ({ taal, zetTaal, t: (sleutel, waarden) => vertaal(taal, sleutel, waarden) }),
    [taal, zetTaal]
  );
  return <TaalContext.Provider value={waarde}>{children}</TaalContext.Provider>;
}

export function useTaal() {
  return useContext(TaalContext);
}
